import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Modal, Skeleton, Spin, Empty } from "antd";
import SpendingDetailRow from "./SpendingDetailRow";
import AmountHistogram from "./AmountHistogram";
import useSpendingDetails from "../../hooks/useSpendingDetails";
import useAmountHistogram from "../../hooks/useAmountHistogram";
import { STATUS_GOOD, STATUS_CRITICAL } from "./palette";

const ScrollArea = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 16px 24px;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const LoadingMoreWrapper = styled.div`
  text-align: center;
  margin-top: 1em;
`;

const HistogramSection = styled.div`
  margin-bottom: 1.25em;
  padding-bottom: 1.25em;
  border-bottom: 1px solid #f0efec;
`;

// 純呈現：接收已查詢好的資料，實際查詢交給 SpendingDetailModal 這個容器負責。
// showCategory 決定每列要顯示分類名稱還是 note（跟 Modal 標題已固定顯示的那個維度相反）。
// sentinelRef 掛在列表最後一個空白標記上，由外層的 IntersectionObserver 監看，
// 進入可視範圍即觸發載入下一頁（無限捲動），不需要使用者手動按按鈕。
export function SpendingDetailModalBody({
  loading,
  spendings,
  canLoadMore,
  sentinelRef,
  showCategory,
}) {
  if (loading && !spendings) {
    return <Skeleton active title={false} paragraph={{ rows: 4 }} />;
  }

  if (!spendings || spendings.length === 0) {
    return <Empty description="沒有符合條件的交易明細" />;
  }

  return (
    <>
      {spendings.map((spending) => (
        <SpendingDetailRow key={spending.id} spending={spending} showCategory={showCategory} />
      ))}
      {canLoadMore && (
        <>
          <Sentinel ref={sentinelRef} aria-hidden />
          {loading && (
            <LoadingMoreWrapper>
              <Spin size="small" />
            </LoadingMoreWrapper>
          )}
        </>
      )}
    </>
  );
}

SpendingDetailModalBody.propTypes = {
  loading: PropTypes.bool,
  spendings: PropTypes.arrayOf(PropTypes.object),
  canLoadMore: PropTypes.bool,
  sentinelRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  showCategory: PropTypes.bool,
};

SpendingDetailModalBody.defaultProps = {
  loading: false,
  spendings: null,
  canLoadMore: false,
  sentinelRef: undefined,
  showCategory: true,
};

function SpendingDetailModalContent({ filter, months, startDate, endDate, scrollRootRef }) {
  const { loading, spendings, loadMore, canLoadMore } = useSpendingDetails(filter, {
    months,
    startDate,
    endDate,
  });
  const { loading: bucketsLoading, buckets } = useAmountHistogram(filter, {
    months,
    startDate,
    endDate,
  });

  const sentinelRef = useRef(null);

  // 每次切換 filter 時這個元件會因 key 改變而重新掛載，捲動位置重置到最上方
  // （ScrollArea 本身不會隨 filter 切換而重新掛載，scrollTop 需要手動歸零）。
  useEffect(() => {
    if (scrollRootRef.current) {
      // ref 的 .current 本來就是可變的逃生艙口，這裡是操作 DOM 節點而非重新賦值 prop 本身
      // eslint-disable-next-line no-param-reassign
      scrollRootRef.current.scrollTop = 0;
    }
  }, [scrollRootRef]);

  useEffect(() => {
    if (loading || !canLoadMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { root: scrollRootRef.current, threshold: [0.1] }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadMore, loading, canLoadMore, scrollRootRef]);

  const barColor = filter.categoryType === "income" ? STATUS_GOOD : STATUS_CRITICAL;

  return (
    <>
      <HistogramSection>
        <AmountHistogram buckets={buckets} loading={bucketsLoading} barColor={barColor} />
      </HistogramSection>
      <SpendingDetailModalBody
        loading={loading}
        spendings={spendings}
        canLoadMore={canLoadMore}
        sentinelRef={sentinelRef}
        showCategory={filter.kind === "note"}
      />
    </>
  );
}

SpendingDetailModalContent.propTypes = {
  filter: PropTypes.shape({
    kind: PropTypes.oneOf(["category", "note"]),
    categoryId: PropTypes.string,
    note: PropTypes.string,
    currencyId: PropTypes.string,
    categoryType: PropTypes.string,
  }).isRequired,
  months: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  scrollRootRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

SpendingDetailModalContent.defaultProps = {
  months: undefined,
  startDate: undefined,
  endDate: undefined,
};

export default function SpendingDetailModal({
  filter,
  months,
  startDate,
  endDate,
  rangeLabel,
  onClose,
}) {
  // filter 本身可攜帶更精確的日期範圍（例如趨勢圖點擊某個月），
  // 有的話優先使用，取代頁面層目前所選的區間。
  const hasFilterRange = Boolean(filter && filter.startDate && filter.endDate);
  const effectiveMonths = hasFilterRange ? undefined : months;
  const effectiveStartDate = hasFilterRange ? filter.startDate : startDate;
  const effectiveEndDate = hasFilterRange ? filter.endDate : endDate;
  const effectiveRangeLabel = (filter && filter.rangeLabel) || rangeLabel;

  const title = filter ? `${filter.label}（${filter.currencyName}・${effectiveRangeLabel}）` : "";
  // key 依 filter 內容重置，讓每次點擊不同分類/備註/月份都是全新的查詢與分頁狀態。
  const contentKey = filter
    ? `${filter.kind}-${filter.categoryId || filter.note}-${filter.currencyId}-${
        filter.startDate || ""
      }-${filter.endDate || ""}`
    : "empty";
  const scrollRootRef = useRef(null);

  return (
    <Modal
      open={Boolean(filter)}
      title={title}
      footer={null}
      onCancel={onClose}
      destroyOnHidden
      centered
      styles={{ body: { padding: 0 } }}
    >
      <ScrollArea ref={scrollRootRef}>
        {filter && (
          <SpendingDetailModalContent
            key={contentKey}
            filter={filter}
            months={effectiveMonths}
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            scrollRootRef={scrollRootRef}
          />
        )}
      </ScrollArea>
    </Modal>
  );
}

SpendingDetailModal.propTypes = {
  filter: PropTypes.shape({
    kind: PropTypes.oneOf(["category", "note"]),
    categoryId: PropTypes.string,
    note: PropTypes.string,
    currencyId: PropTypes.string,
    currencyName: PropTypes.string,
    categoryType: PropTypes.string,
    label: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    rangeLabel: PropTypes.string,
  }),
  months: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  rangeLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

SpendingDetailModal.defaultProps = {
  filter: null,
  months: undefined,
  startDate: undefined,
  endDate: undefined,
  rangeLabel: "",
};
